import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { fn } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import { service } from "@ember/service";
import getURL from "discourse/lib/get-url";
import { eq } from "discourse/truth-helpers";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";

export default class ModMarketGallery extends Component {
  @service a11y;

  @tracked selectedIndex = 0;

  get items() {
    if (this.args.items?.length) {
      return this.args.items;
    }

    return [{ type: "image", url: null, alt: i18n("discourse_mod_market.gallery.image_placeholder") }];
  }

  get selected() {
    return this.items[this.selectedIndex] || this.items[0];
  }

  get hasMultipleItems() {
    return this.items.length > 1;
  }

  get previousIndex() {
    return this.selectedIndex === 0
      ? this.items.length - 1
      : this.selectedIndex - 1;
  }

  get nextIndex() {
    return this.selectedIndex === this.items.length - 1
      ? 0
      : this.selectedIndex + 1;
  }

  @action
  select(index) {
    this.selectedIndex = index;
    this.a11y.announce(
      i18n("discourse_mod_market.gallery.selected", {
        current: index + 1,
        total: this.items.length,
      }),
      "polite"
    );
  }

  <template>
    <section class="mod-market-gallery" aria-label={{i18n "discourse_mod_market.gallery.label"}} ...attributes>
      {{#if this.selected.url}}
        <img
          class="mod-market-gallery__backdrop"
          src={{this.selected.url}}
          alt=""
          aria-hidden="true"
        />
      {{/if}}
      <div class="mod-market-gallery__stage">
        {{#if this.selected.url}}
          {{#if (eq this.selected.type "image")}}
            <img src={{this.selected.url}} alt={{this.selected.alt}} />
          {{else}}
            <video src={{this.selected.url}} controls>
              {{i18n "discourse_mod_market.gallery.video_fallback"}}
            </video>
          {{/if}}
        {{else}}
          <div class="mod-market-gallery__placeholder">
            {{dIcon (if (eq this.selected.type "video") "play" "image")}}
            <span>{{i18n
                (if
                  (eq this.selected.type "video")
                  "discourse_mod_market.gallery.video_placeholder"
                  "discourse_mod_market.gallery.image_placeholder"
                )
              }}</span>
          </div>
        {{/if}}
      </div>

      {{#if this.hasMultipleItems}}
        <button
          type="button"
          class="mod-market-gallery__previous"
          aria-label={{i18n "discourse_mod_market.gallery.previous"}}
          {{on "click" (fn this.select this.previousIndex)}}
        >
          <img
            class="mod-market-gallery__navigation-icon"
            src={{getURL
              "/plugins/discourse-mod-market/images/mod-detail-gallery-arrow.svg"
            }}
            alt=""
          />
        </button>
        <button
          type="button"
          class="mod-market-gallery__next"
          aria-label={{i18n "discourse_mod_market.gallery.next"}}
          {{on "click" (fn this.select this.nextIndex)}}
        >
          <img
            class="mod-market-gallery__navigation-icon"
            src={{getURL
              "/plugins/discourse-mod-market/images/mod-detail-gallery-arrow.svg"
            }}
            alt=""
          />
        </button>
      {{/if}}

      <div class="mod-market-gallery__pagination">
        {{#each this.items as |item index|}}
          <button
            type="button"
            class={{dConcatClass
              "mod-market-gallery__dot"
              (if (eq index this.selectedIndex) "is-selected")
            }}
            aria-label={{i18n "discourse_mod_market.gallery.select" name=item.alt}}
            aria-current={{if (eq index this.selectedIndex) "true"}}
            {{on "click" (fn this.select index)}}
          ></button>
        {{/each}}
      </div>
    </section>
  </template>
}
